<script setup>
import { Link, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted, computed } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    allBookings: Object,
    filters: Object,
    flash: Object,
    user: Object,
});

let bookingSearch = ref(props.filters.bookingSearch);
const $toast = useToast();
dayjs.extend(advancedFormat);
let isLoading = ref(false);

watch(bookingSearch, debounce(function (value) {
    router.get('/other-bookings', { bookingSearch: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 700));

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

// Calculate duration of stay
const calculateDuration = (arrival, departure) => {
    const start = dayjs(arrival);
    const end = dayjs(departure);
    return end.diff(start, 'day');
};

// Format date to be more readable
const formatDate = (date) => {
    return dayjs(date).format('Do MMM YYYY');
};

// Truncate text with ellipsis
const truncateText = (text, length = 30) => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
};

const getInitials = (name) => {
    if (!name) return '';
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};

// Get status badge class
const getStatusBadgeClass = (status) => {
    const statusMap = {
        'Pending': 'bg-label-dark',
        'SendConfirmation': 'bg-label-warning',
        'Confirmed': 'bg-label-info',
        'Cancelled': 'bg-label-danger',
        'Paid': 'bg-label-success'
    };
    return statusMap[status] || 'bg-label-secondary';
};
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";

.booking-details {
    font-size: 0.85rem;
}

.booking-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
}

.booking-meta i {
    font-size: 0.9rem;
}

.hotel-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    background-color: #f0f0f0;
    margin-right: 4px;
    margin-bottom: 4px;
    font-size: 0.75rem;
}

.duration-badge {
    background-color: #e8f5e9;
    color: #2e7d32;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    display: inline-flex;
    align-items: center;
}

.agent-info {
    display: flex;
    align-items: center;
    gap: 6px;
}

.agent-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 600;
}
</style>
<template>
    <AppLayout title="Bookings Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">
                                <h5 class="card-title mb-0">Other Destinations Booking Manager</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3 pt-md-0">
                                <div class="dt-buttons" v-if="$page.props.user.permissions.includes('add-bookings')">
                                    <Link type="button" class="btn btn-warning" style="margin-right: 10px;"
                                        href="/bookings/createbali/" preserve-scroll>
                                    <i class='bx bx-plus bx-tada'></i> Add New Booking
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-12 col-md-12 d-flex justify-content-center justify-content-md-end">
                                <div class="dataTables_filter">
                                    <label>Search:
                                        <input v-model="bookingSearch" type="text" class="form-control"
                                            placeholder="Type Booking Number, Hotel or Agent"></label>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Booking Details</th>
                                    <th>Stay Information</th>
                                    <th>Agent & Dates</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="(booking, index) in allBookings.data" :key="booking.id">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="d-flex flex-column">
                                                <span class="emp_name text-truncate fw-bold">{{ booking.booking_no
                                                    }}</span>
                                                <div class="booking-meta">
                                                    <i class='bx bx-building-house'></i>
                                                    <span v-if="booking.stay_type == 0" class="text-muted">Single
                                                        Stay</span>
                                                    <span v-if="booking.stay_type == 1" class="text-muted">Split
                                                        Stay</span>
                                                </div>
                                                <div class="booking-meta" v-if="booking.hotel_included === 'yes'">
                                                    <i class='bx bx-hotel'></i>
                                                    <span class="text-muted">Hotels Included</span>
                                                </div>
                                                <div class="booking-meta" v-if="booking.sight_seeing">
                                                    <i class='bx bx-camera'></i>
                                                    <span class="text-muted">Sight Seeing</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div class="booking-details">
                                            <!-- Show hotel information if available -->
                                            <div
                                                v-if="booking.otherbookinglocations && booking.otherbookinglocations.length > 0">
                                                <div v-for="(location, locIndex) in booking.otherbookinglocations.slice(0, 2)"
                                                    :key="locIndex" class="mb-1">
                                                    <div class="hotel-tag">
                                                        <i class='bx bx-hotel'></i> {{ truncateText(location.hotel_name,
                                                            20) }}
                                                    </div>
                                                    <small v-if="location.nights" class="text-muted ms-1">
                                                        {{ location.nights }} night{{ location.nights !== 1 ? 's' : ''
                                                        }}
                                                    </small>
                                                </div>
                                                <small v-if="booking.otherbookinglocations.length > 2"
                                                    class="text-muted">
                                                    +{{ booking.otherbookinglocations.length - 2 }} more hotels
                                                </small>
                                            </div>

                                            <!-- Show car information if available -->
                                            <div v-if="booking.car_used" class="mt-2">
                                                <small class="text-muted">
                                                    <i class='bx bx-car'></i> {{ truncateText(booking.car_used, 25) }}
                                                </small>
                                            </div>

                                            <!-- Show total duration -->
                                            <div class="mt-2">
                                                <span class="duration-badge">
                                                    <i class='bx bx-calendar me-1'></i>
                                                    {{ calculateDuration(booking.arrival_date, booking.departure_date)
                                                    }} days
                                                </span>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <!-- Agent information -->
                                        <div class="agent-info mb-2" v-if="booking.agent">
                                            <div class="agent-avatar">
                                                {{ getInitials(booking.agent.agency_name) }}
                                            </div>
                                            <span class="text-truncate">{{ truncateText(booking.agent.agency_name, 20)
                                                }}</span>
                                        </div>

                                        <!-- Date information -->
                                        <div class="booking-details">
                                            <div class="mb-1">
                                                <i class='bx bx-calendar-check text-primary'></i>
                                                <small>{{ formatDate(booking.arrival_date) }}</small>
                                            </div>
                                            <div>
                                                <i class='bx bx-calendar-x text-danger'></i>
                                                <small>{{ formatDate(booking.departure_date) }}</small>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <span :class="['badge', getStatusBadgeClass(booking.status)]">
                                            {{ booking.status === 'SendConfirmation' ? 'Awaiting Confirmation' :
                                                booking.status }}
                                        </span>

                                        <!-- Payment deadline if available -->
                                        <div v-if="booking.payment_deadline" class="mt-2">
                                            <small class="text-muted d-flex align-items-center">
                                                <i class='bx bx-money-withdraw me-1'></i>
                                                Due: {{ formatDate(booking.payment_deadline) }}
                                            </small>
                                        </div>
                                    </td>

                                    <td>
                                        <div class="d-flex">
                                            <Link :href="'/other-bookings/show/' + booking.id"
                                                v-if="$page.props.user.permissions.includes('booking-manager')"
                                                class="btn btn-icon btn-label-secondary" preserve-state
                                                data-bs-toggle="tooltip" data-bs-placement="top" title="View Details">
                                            <i class="bx bx-search-alt"></i>
                                            </Link>
                                        </div>
                                    </td>
                                </tr>

                                <!-- Empty state when no bookings found -->
                                <tr v-if="allBookings.data.length === 0">
                                    <td colspan="5" class="text-center py-4">
                                        <div class="text-muted">
                                            <i class='bx bx-search bx-lg mb-2'></i>
                                            <p>No bookings found matching your search criteria.</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status"
                                    aria-live="polite">
                                    Showing {{ allBookings.from }} to {{ allBookings.to }} of {{
                                        allBookings.total
                                    }} Bookings</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="allBookings.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>